import { CategoriesModel } from './categories.model';
import { CategoryItem } from './categories.types';

export class CategoriesController {
  /**
   * Returns sorted list of categories hierarchically or as a flat list with parenthood metadata.
   */
  public static list(searchQuery: string = ''): CategoryItem[] {
    const all = CategoriesModel.getCategories();
    const query = searchQuery.trim().toLowerCase();

    // If search is specified, filter by name
    if (query) {
      return all.filter(c => c.name.toLowerCase().includes(query));
    }

    // Otherwise, return them hierarchically structured (main first, then their children immediately following)
    // or return a flat sorted list: parents first, then their child elements
    const result: CategoryItem[] = [];
    const parents = all.filter(p => p.parentId === null);

    parents.forEach(p => {
      result.push(p);
      const subcats = all.filter(c => c.parentId === p.id);
      subcats.forEach(sub => {
        result.push(sub);
      });
    });

    return result;
  }

  /**
   * Returns list from database, optionally filtered.
   */
  public static async listFromServer(searchQuery: string = ''): Promise<CategoryItem[]> {
    const all = await CategoriesModel.fetchCategories();
    const query = searchQuery.trim().toLowerCase();

    if (query) {
      return all.filter(c => c.name.toLowerCase().includes(query));
    }

    const result: CategoryItem[] = [];
    const parents = all.filter(p => p.parentId === null);

    parents.forEach(p => {
      result.push(p);
      const subcats = all.filter(c => c.parentId === p.id);
      subcats.forEach(sub => {
        result.push(sub);
      });
    });

    return result;
  }

  /**
   * Accepts FormData to create a Category
   */
  public static handleCreate(formData: FormData): { success: boolean; error?: string } {
    const name = formData.get('name') as string || '';
    const parentIdVal = formData.get('parentId') as string || '';
    const parentId = parentIdVal === 'main' || !parentIdVal ? null : parseInt(parentIdVal, 10);

    return CategoriesModel.addCategory(name, parentId);
  }

  /**
   * Accepts FormData to create a Category on MySQL server
   */
  public static async handleCreateToServer(formData: FormData): Promise<{ success: boolean; error?: string }> {
    const name = formData.get('name') as string || '';
    const parentIdVal = formData.get('parentId') as string || '';
    const parentId = parentIdVal === 'main' || !parentIdVal ? null : parseInt(parentIdVal, 10);

    return await CategoriesModel.addCategoryToServer(name, parentId);
  }

  /**
   * Accepts FormData to edit a Category
   */
  public static handleUpdate(id: number, formData: FormData): { success: boolean; error?: string } {
    const name = formData.get('name') as string || '';
    const parentIdVal = formData.get('parentId') as string || '';
    const parentId = parentIdVal === 'main' || !parentIdVal ? null : parseInt(parentIdVal, 10);

    return CategoriesModel.updateCategory(id, name, parentId);
  }

  /**
   * Accepts FormData to edit a Category on MySQL server
   */
  public static async handleUpdateOnServer(id: number, formData: FormData): Promise<{ success: boolean; error?: string }> {
    const name = formData.get('name') as string || '';
    const parentIdVal = formData.get('parentId') as string || '';
    const parentId = parentIdVal === 'main' || !parentIdVal ? null : parseInt(parentIdVal, 10);

    return await CategoriesModel.updateCategoryOnServer(id, name, parentId);
  }

  /**
   * Removes a category
   */
  public static handleDelete(id: number): { success: boolean; error?: string } {
    return CategoriesModel.deleteCategory(id);
  }

  /**
   * Removes a category on MySQL server
   */
  public static async handleDeleteOnServer(id: number): Promise<{ success: boolean; error?: string }> {
    return await CategoriesModel.deleteCategoryFromServer(id);
  }

  /**
   * Validates if a category can accept a subcategory (less than 2 exists and is not a subcategory itself)
   */
  public static canAcceptSubcategory(id: number): { allowed: boolean; reason?: string } {
    const list = CategoriesModel.getCategories();
    const item = list.find(c => c.id === id);
    if (!item) {
      return { allowed: false, reason: 'Categoria não existe.' };
    }
    if (item.parentId !== null) {
      return { allowed: false, reason: 'Esta categoria já é uma subcategoria e não pode conter outras subcategorias.' };
    }
    const children = list.filter(c => c.parentId === id);
    if (children.length >= 2) {
      return { allowed: false, reason: 'Esta categoria já possui o limite máximo de 2 subcategorias.' };
    }
    return { allowed: true };
  }
}
